import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import NetworkTable, {
  ColumnConfig,
  ColumnType,
} from "../../components/InterfacesTable/NetworkTable/NetworkTable";
import LabelValueDisplay from "../../components/LabelValue/LabelValueDisplay";
import AddButton from "../../components/AddButton/AddButton";
import DeleteIcon from "../../../../../asset/image/delete2.svg";
import AddEditInterfaceModal from "../../components/Modals/AddEditInterfaceModal";
import { toast } from "react-toastify";

interface IdentityRuleEditorProps {
  identityRule: any;
  identityWANoptions: any[];
  onChange: (rule: any) => void;
  onDeleteClick: () => void;
  identityRuleList?: any[];
}

const IdentityRuleEditor: React.FC<IdentityRuleEditorProps> = ({
  identityRule,
  identityWANoptions,
  onChange,
  onDeleteClick,
  identityRuleList,
}) => {
  /* ---------------- LOCAL STATE ---------------- */

  const [isTrafficPolicyModalOpen, setIsTrafficPolicyModalOpen] = useState(false);
  const [isEditingTrafficPolicy, setIsEditingTrafficPolicy] = useState(false);
  const [editingTrafficPolicyId, setEditingTrafficPolicyId] = useState<string | null>(null);

  const [trafficPolicyForm, setTrafficPolicyForm] = useState({
    alias_tmp: "",
    uploadRate: "",
    downloadRate: "",
  });

  /* ---------------- TABLE CONFIG ---------------- */

  const trafficPolicyColumns: ColumnConfig[] = [
    {
      label: "WAN",
      key: "alias_tmp",
      type: ColumnType.text,
      maxWidth: "120px",
    },
    {
      label: "Upload Rate (Mbps)",
      key: "uploadRate",
      type: ColumnType.text,
      maxWidth: "140px",
    },
    {
      label: "Download Rate (Mbps)",
      key: "downloadRate",
      type: ColumnType.text,
      maxWidth: "160px",
    },
    {
      label: "Actions",
      key: "custom",
      type: ColumnType.actions,
      actions: ["setting", "delete"],
      maxWidth: "80px",
    },
  ];

  /* ---------------- WAN OPTIONS ---------------- */

  const availableWANOptions = useMemo(() => {
    const usedWanIds = new Set(
      (identityRule?.rules || []).map((r) => Number(r?.wan_id))
        .filter(Number.isFinite)
    );

    return identityWANoptions.map((wan) => ({
      ...wan,
      disabled:
        !isEditingTrafficPolicy && usedWanIds.has(wan.id),
    }));
  }, [identityRule?.rules, identityWANoptions, isEditingTrafficPolicy]);

  /* ---------------- HANDLERS ---------------- */

  const handleOpenTrafficPolicyModal = (policy?: any) => {
    if (policy?.id) {
      setIsEditingTrafficPolicy(true);
      setEditingTrafficPolicyId(policy.id);
      setTrafficPolicyForm({
        alias_tmp: policy.alias_tmp,
        uploadRate: policy.uploadRate,
        downloadRate: policy.downloadRate,
      });
    } else {
      setIsEditingTrafficPolicy(false);
      setEditingTrafficPolicyId(null);
      setTrafficPolicyForm({
        alias_tmp: "",
        uploadRate: "",
        downloadRate: "",
      });
    }

    setIsTrafficPolicyModalOpen(true);
  };

  const handleCloseTrafficPolicyModal = () => {
    setIsTrafficPolicyModalOpen(false);
    setIsEditingTrafficPolicy(false);
    setEditingTrafficPolicyId(null);
    setTrafficPolicyForm({
      alias_tmp: "",
      uploadRate: "",
      downloadRate: "",
    });
  };

  const handleTrafficPolicyChange = (key: string) => (e: any) => {
    let value = e.target.value;

    // Allow only whole numbers for rate fields
    if (key === "uploadRate" || key === "downloadRate") {
      value = value.replace(/[^0-9]/g, "").slice(0, 20);
    }
    setTrafficPolicyForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSaveTrafficPolicy = () => {
    const selectedWan = identityWANoptions.find(
      (w) => w.label === trafficPolicyForm.alias_tmp
    );
    if (!selectedWan) return;

    const newPolicy = {
      id: `${selectedWan.id}_${selectedWan.link_type}`,
      alias_tmp: selectedWan.label,
      uploadRate: trafficPolicyForm.uploadRate,
      downloadRate: trafficPolicyForm.downloadRate,
      wan_id: selectedWan.id,
      wan_link_type: selectedWan.interface_type === "WIFI"?  selectedWan.interface_type:  selectedWan.link_type,
    };

    let updatedRules: any[] = [];

    if (isEditingTrafficPolicy && editingTrafficPolicyId) {
      updatedRules = (identityRule.rules || []).map((r) =>
        r.id === editingTrafficPolicyId ? newPolicy : r
      );
    } else {
      updatedRules = [...(identityRule.rules || []), newPolicy];
    }

    onChange({
      ...identityRule,
      rules: updatedRules,
    });

    handleCloseTrafficPolicyModal();
  };

  const handleTrafficPolicyActionClick = (
    type: "setting" | "delete",
    _headerKey: string,
    row: any
  ) => {
    if (type === "setting") {
      handleOpenTrafficPolicyModal(row);
      return;
    }

    if (type === "delete") {
      const updatedRules = (identityRule.rules || []).filter(
        (r) => r.id !== row.id
      );

      onChange({
        ...identityRule,
        rules: updatedRules,
      });
    }
  };

  /* ---------------- RENDER ---------------- */

  if (!identityRule) return null;

  return (
    <Box sx={{ flex: 1, paddingLeft: 2, border: "1px solid #d1d5db", borderRadius: "4px", marginLeft: "16px", backgroundColor: "#fff", paddingTop: "0px", height: "345px" }} className="wan-tab-grid-container wantab-right-panel">
      <div className="wan-tab-grid">
        {identityRuleList && identityRuleList.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3, mt: 1, maxWidth: "260px" }}>
            <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500 }}>
              {identityRule.id ? "Edit " : "Add "} Identity Rule
            </Typography>
            <Box sx={{ margin: "0 auto", paddingTop: 1.5, mb: 1 }}>
              <LabelValueDisplay
                label="Rule Name"
                isInput
                value={identityRule.name || ""}
                onChange={(e) =>{
                  const value = e.target.value;

                  if (value.length > 32) {
                    toast.error("Rule Name should not exceed 32 characters", {
                      position: toast.POSITION.BOTTOM_LEFT,
                    })
                    
                    return;
                  }

                  onChange({ ...identityRule, name: value });
                }
                  

                }
                placeholder="Enter rule name"
              />
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: "10px" }}>
          {
            identityRule && (
              <Box
                component="button"
                onClick={onDeleteClick}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  p: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                }}
                title="Delete Identity Rule"
              >
                <Box component="img" src={DeleteIcon} alt="Delete" sx={{ width: 16, height: 18 }} />
              </Box>
            )
          }
        </Box>

      </div>
      <Box sx={{ margin: "0 auto", paddingTop: 1.5, mb: 1 }}>
        <LabelValueDisplay
          label="Notes / Description"
          isInput
          isMultiline
          textInputRows={3}
          value={identityRule.description || ""}
          onChange={(e) =>
            onChange({ ...identityRule, description: e.target.value })
          }
          placeholder="Enter description"
        />
      </Box>
      <Box sx={{ mt: 5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
            alignItems: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 500 }}>
            Traffic Policy Profile
          </Typography>
          <div>
            <AddButton
              onAddClick={() => handleOpenTrafficPolicyModal()}
              addLabel="+ Add"
              maxWidth="80px"
            />
          </div>
        </Box>

        <NetworkTable
          data={identityRule.rules || []}
          columns={trafficPolicyColumns}
          showFilterBar={false}
          onActionClick={handleTrafficPolicyActionClick}
          minwidth={600}
        />
      </Box>



      {/* ADD / EDIT TRAFFIC POLICY MODAL */}
      <AddEditInterfaceModal
        open={isTrafficPolicyModalOpen}
        onClose={handleCloseTrafficPolicyModal}
        onSave={handleSaveTrafficPolicy}
        saveText={isEditingTrafficPolicy ? "Update" : "Add"}
        disableSave={
          !trafficPolicyForm.alias_tmp ||
          Number(trafficPolicyForm.uploadRate) <= 0 ||
          Number(trafficPolicyForm.downloadRate) <= 0
        }
        title={
          isEditingTrafficPolicy
            ? "Edit Traffic Policy Profile"
            : "Add Traffic Policy Profile"
        }
        layout={[
          {
            columns: [
              {
                component: (
                  <LabelValueDisplay
                    label="WAN"
                    isDropdown
                    dropdownOptions={availableWANOptions.map((w) => w.label)}
                    disabledOptions={availableWANOptions
                      .filter((w) => w.disabled)
                      .map((w) => w.label)}
                    value={trafficPolicyForm.alias_tmp}
                    onChange={handleTrafficPolicyChange("alias_tmp")}
                    placeholder="Select WAN"
                    disabled={isEditingTrafficPolicy}
                  />
                ),
                gridSize: 12,
              },
            ],
          },
          {
            columns: [
              {
                component: (
                  <LabelValueDisplay
                    label="Upload Rate (Mbps)"
                    isInput
                    type="number"
                    value={trafficPolicyForm.uploadRate}
                    onChange={handleTrafficPolicyChange("uploadRate")}
                    placeholder="Enter upload rate"
                  />
                ),
                gridSize: 6,
              },
              {
                component: (
                  <LabelValueDisplay
                    label="Download Rate (Mbps)"
                    isInput
                    type="number"
                    value={trafficPolicyForm.downloadRate}
                    onChange={handleTrafficPolicyChange("downloadRate")}
                    placeholder="Enter download rate"
                  />
                ),
                gridSize: 6,
              },
            ],
          },
        ]}
      />
    </Box>
  );
};

export default IdentityRuleEditor;
